import { ObjectPosition } from 'pages/Batch/sections/Sidebar/EditorRemoveBgTool/MobilePanel/constants';

/**
 * Computes image offset coords to position the detected object at the given anchor point.
 * Object detection is based on color difference with the top-left pixel.
 */
const computeImageOffsets = async (imgElement: HTMLImageElement, position: ObjectPosition = ObjectPosition.CENTER) => {
  return new Promise<{ offsetX: number; offsetY: number }>(resolve => {
    const canvas = document.createElement('canvas');
    canvas.width = imgElement.naturalWidth;
    canvas.height = imgElement.naturalHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) return resolve({ offsetX: 0, offsetY: 0 });

    ctx.drawImage(imgElement, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data, width, height } = imageData;

    const idxTL = 0;
    const topLeftR = data[idxTL];
    const topLeftG = data[idxTL + 1];
    const topLeftB = data[idxTL + 2];
    const topLeftA = data[idxTL + 3];

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        const a = data[idx + 3];

        if (r !== topLeftR || g !== topLeftG || b !== topLeftB || a !== topLeftA) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (minX > maxX || minY > maxY) {
      // No different pixels found; object not detected
      return resolve({ offsetX: 0, offsetY: 0 });
    }

    const objectWidth = maxX - minX;
    const objectHeight = maxY - minY;

    let targetX: number;
    let targetY: number;

    // Horizontal anchor
    switch (position) {
      case ObjectPosition.TOP_LEFT:
      case ObjectPosition.BOTTOM_LEFT:
        targetX = 0;
        break;
      case ObjectPosition.TOP_RIGHT:
      case ObjectPosition.BOTTOM_RIGHT:
        targetX = width - objectWidth;
        break;
      default:
        targetX = width / 2 - objectWidth / 2;
        break;
    }

    // Vertical anchor
    switch (position) {
      case ObjectPosition.TOP_LEFT:
      case ObjectPosition.TOP:
      case ObjectPosition.TOP_RIGHT:
        targetY = 0;
        break;
      case ObjectPosition.BOTTOM_LEFT:
      case ObjectPosition.BOTTOM:
      case ObjectPosition.BOTTOM_RIGHT:
        targetY = height - objectHeight;
        break;
      default:
        targetY = height / 2 - objectHeight / 2;
        break;
    }

    const offsetX = targetX - minX;
    const offsetY = targetY - minY;

    resolve({ offsetX, offsetY });
  });
};

export default computeImageOffsets;
